import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import VastuNavbar from './VastuNavbar';
import VastuSidebar from './VastuSidebar';
import useIsMobile from '../hooks/useIsMobile';

const VastuLayout = ({ children, currentRoute, navigate }) => {
  const location = useLocation();
  const isEditorRoute = location.pathname.startsWith('/editor');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  // My Projects has its own phone-width header (logo/title row + its own
  // settings icon opening the account sheet) — this global navbar would
  // just duplicate it, per explicit request. Separate from the 992px
  // `isMobile` above (that one only drives the sidebar); this is the same
  // 768px phone breakpoint ProjectsPage itself uses. Scoped to just the
  // Projects route so every other not-yet-redesigned page keeps this
  // navbar as its only nav on phone widths.
  const isNarrowMobile = useIsMobile();
  const isProjectsRoute = location.pathname === '/' || location.pathname === '/projects';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth < 992) {
        setSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  return (
    <div className="vastu-layout d-flex min-vh-100 position-relative">
      {/* Sidebar */}
      <VastuSidebar 
        collapsed={sidebarCollapsed}
        currentRoute={currentRoute}
        navigate={navigate}
        onLinkClick={closeSidebar}
      />

      {/* Main Content */}
      <div className={`main-content flex-fill d-flex flex-column ${
        sidebarCollapsed ? 'sidebar-main-expanded' : 'sidebar-main-collapsed'
      }`}>

        {/* Global Navbar — hidden on editor pages (Editor has its own topbar)
            and on the Projects page at phone widths (it has its own header). */}
        {!isEditorRoute && !(isProjectsRoute && isNarrowMobile) && <VastuNavbar />}

        {/* Page Content */}
        <main className="page-content flex-fill overflow-auto">
          <div className="container-fluid p-0 m-0">
            {children ? children : <Outlet />}
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobile && !sidebarCollapsed && (
        <div 
          className="mobile-overlay position-fixed top-0 start-0 w-100 h-100 d-lg-none"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}

      <style jsx>{`
        .vastu-layout {
          background: var(--bg-app);
          min-height: 100vh;
        }

        .main-content {
          transition: all 0.3s ease;
        }

        .sidebar-main-expanded {
          margin-left: 0;
        }

        .sidebar-main-collapsed {
          margin-left: 260px;
        }

        .page-content {
          background: var(--bg-app);
          position: relative;
          min-height: calc(100vh - 80px);
        }

        .mobile-overlay {
          background: rgba(20, 30, 60, 0.4);
          backdrop-filter: blur(4px);
          z-index: 1040;
        }

        @media (max-width: 991.98px) {
          .sidebar-main-expanded,
          .sidebar-main-collapsed {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default VastuLayout;